<script setup lang="ts">
const props = defineProps({ blok: Object })

const fields = computed(() => {
    return [
        props.blok?.fields_1,
        props.blok?.fields_2,
        props.blok?.fields_3,
    ]
})

const realities = computed(() => {
    return [
        {
            title: props.blok?.reality_1_title,
            content: props.blok?.reality_1_content,
        },
        {
            title: props.blok?.reality_2_title,
            content: props.blok?.reality_2_content,
        },
        {
            title: props.blok?.reality_3_title,
            content: props.blok?.reality_3_content,
        },
    ]
})
</script>

<template>
    <section class="relative py-20">
        <div class="absolute top-0 left-0 w-full h-[300px] bg-gradient-to-b from-chsc-raw to-transparent z-[-1]" />
        <img
            v-motion-slide-visible-once-bottom
            :src="blok?.image.filename"
            :alt="blok?.image.alt"
            class="absolute top-6 right-6 z-[-1] max-[1025px]:hidden"
        >
        <UiContainer max-width="max-w-3xl">
            <header class="insight-category-header">
                <img
                    :src="blok?.icon.filename"
                    :alt="blok?.icon.alt"
                >
                <StoryblokRichText :doc="blok?.header" />
            </header>
        </UiContainer>
        <UiContainer>
            <div class="grid grid-cols-2 gap-x-20 items-center">
                <div class="flex flex-col items-center justify-center">
                    <!-- <p class="font-londrina-solid fs-5xl text-chsc-indigo-500 text-center">
                        {{ blok?.claim }}
                    </p> -->
                </div>
            </div>
        </UiContainer>
        <UiContainer>
            <div
                v-motion-slide-visible-once-bottom
                class="w-full relative mt-20 border-[2px] border-chsc-indigo-500 rounded-[40px] p-12"
            >
                <h4 class="absolute top-0 left-12 -translate-y-1/2 bg-white px-4 fs-4xl font-londrina-solid text-chsc-indigo-500 mb-6">
                    {{ blok?.benefits_title }}
                </h4>
                <div class="grid grid-cols-4 gap-8 [&>div>p]:font-londrina-solid [&>div>h5]:mb-4 [&>div>h5]:font-londrina-solid [&>div>h5]:fs-5xl [&>div>h5]:text-chsc-yellow [&>div>h5]:leading-[32px] [&>div>p]:fs-4xl [&>div>p]:text-chsc-indigo-500 [&>div>p]:leading-[32px] [&>div>p>strong]:text-chsc-yellow [&>div>p>strong]:font-normal max-[769px]:grid-cols-2 max-[641px]:grid-cols-1 max-[641px]:gap-x-0 max-[641px]:gap-y-6">
                    <div>
                        <StoryblokRichText :doc="blok?.benefits_a" />
                    </div>
                    <div>
                        <StoryblokRichText :doc="blok?.benefits_b" />
                    </div>
                    <div>
                        <StoryblokRichText :doc="blok?.benefits_c" />
                    </div>
                    <div>
                        <StoryblokRichText :doc="blok?.benefits_d" />
                    </div>
                </div>
            </div>
        </UiContainer>
        <UiContainer max-width="max-w-2xl">
            <div class="mt-20 insighr-category-description text-center">
                <StoryblokRichText :doc="blok?.description" />
            </div>
        </UiContainer>
        <UiContainer max-width="max-w-3xl">
            <div class="grid grid-cols-3 gap-x-6 max-[641px]:grid-cols-1 max-[641px]:gap-x-0 max-[641px]:gap-y-6">
                <div
                    v-for="field in fields"
                    :key="field"
                    class="relative mt-20"
                >
                    <h6 class="font-londrina-solid text-center fs-xl text-chsc-indigo-500 mb-4">
                        {{ field }}
                    </h6>
                    <svg
                        viewBox="0 0 42 60"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-[60px] mx-auto absolute top-full left-1/2 -translate-x-1/2 -translate-y-1/3 z-[-1]"
                    >
                        <path
                            d="M10.3636 3.7726e-07L30.5455 1.36594e-07L30.5455 41.3895L42 41.3895L19.6364 60L4.93565e-07 41.3895L10.3636 41.3895L10.3636 3.7726e-07Z"
                            fill="#FFC14A"
                        />
                    </svg>
                </div>
            </div>
        </UiContainer>
        <UiContainer>
            <img
                v-motion-slide-visible-once-top
                :src="blok?.fields_image.filename"
                :alt="blok?.fields_image.alt"
                class="mx-auto"
            >
        </UiContainer>
        <UiContainer>
            <header class="mb-16">
                <h3 class="mt-20 font-londrina-solid text-center fs-4xl text-chsc-indigo-500">
                    {{ blok?.reality_title }}
                </h3>
            </header>
            <div class="grid grid-cols-2 gap-x-20 max-[769px]:grid-cols-1 max-[769px]:gap-x-0 max-[769px]:gap-y-10">
                <div class="flex flex-col gap-y-12 px-12 max-[769px]:px-0">
                    <div
                        v-for="reality in realities"
                        :key="reality.title"
                        v-motion-slide-visible-once-bottom
                        class="w-full relative border-[2px] border-chsc-indigo-500 rounded-[40px] px-6 py-8"
                    >
                        <h4 class="absolute top-0 left-12 -translate-y-1/2 bg-white px-4 fs-3xl font-londrina-solid text-chsc-indigo-500 mb-6">
                            {{ reality.title }}
                        </h4>
                        <div class="[&>p]:font-londrina-solid [&>p]:leading-[20px] [&>p]:fs-2xl [&>p]:text-chsc-indigo-500 [&>p>strong]:fs-4xl [&>p>strong]:text-chsc-yellow [&>p>strong]:uppercase">
                            <StoryblokRichText :doc="reality.content" />
                        </div>
                    </div>
                </div>
                <div class="insighr-category-description">
                    <StoryblokRichText :doc="blok?.reality_description" />
                </div>
            </div>
        </UiContainer>
    </section>
</template>
